import React, { useMemo } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { MealUtils, type MealLike } from '@fytness/shared';
import { GetMealDocument, type Meal } from '~/graphql/generated/graphql';
import { MealNutritionFacts } from '~/meal/components/meal-nutrition-facts/MealNutritionFacts';
import { PresentationCaption } from './PresentationCaption';

interface MealCardProps {
  caption?: string | null;
  meal: Meal;
}

interface MealCardSyncedProps {
  payload: Record<string, unknown>;
}

interface MealPayload {
  caption?: string | null;
  mealId?: string;
}

/**
 * `present_meal` → a read-only catalog-food card showing per-serving nutrition.
 * Meal nutrition lives in GraphQL (no local Meal table), so it is fetched via
 * `GetMealDocument`. Not editable (a catalog meal has no logged amount).
 */
export const MealCard: React.FC<MealCardProps> & {
  Synced: React.FC<MealCardSyncedProps>;
} = ({ caption, meal }) => {
  const nutrition = useMemo(
    () => MealUtils.adjustedNutrition(meal as MealLike, meal.servingSize ?? 0),
    [meal],
  );

  return (
    <View className="w-full py-2 gap-2">
      <PresentationCaption caption={caption} />
      <View className="rounded-2xl bg-surface p-5">
        <MealNutritionFacts
          name={meal.name}
          brand={meal.brand}
          nutrition={nutrition}
        />
      </View>
    </View>
  );
};

const MealCardSynced: React.FC<MealCardSyncedProps> = ({ payload }) => {
  const { t } = useTranslation('meal');
  const { caption, mealId } = payload as MealPayload;

  const { data, loading } = useQuery(GetMealDocument, {
    variables: { id: mealId as string },
    skip: !mealId,
  });

  const meal = data?.meal as Meal | undefined;

  if (!mealId) {
    return null;
  }

  if (loading) {
    return (
      <View className="w-full py-2 gap-2">
        <PresentationCaption caption={caption} />
        <View className="rounded-2xl bg-surface p-5">
          <Text className="text-text-muted text-sm">{t('loading')}</Text>
        </View>
      </View>
    );
  }

  if (!meal) {
    return null;
  }

  return <MealCard caption={caption} meal={meal} />;
};

MealCard.Synced = MealCardSynced;
